import { DateTime } from "luxon";
import { isNull } from "lodash";

/**
 * Variable constants
 */
export const weekdays = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const weekdaysAbrv = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

export const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const monthAbrv = [
  "Jan",
  "Feb",
  "March",
  "April",
  "May",
  "June",
  "July",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
];

export const monthNumbers = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
];

export const durationLabels = [
  "0 min",
  "15 min",
  "30 min",
  "45 min",
  "60 min",
  "1 hr 15 min",
  "1 hr 30 min",
  "1 hr 45 min",
  "2 hrs",
];

export const durationOptions = [
  { value: 15, label: "15 min" },
  { value: 30, label: "30 min" },
  { value: 45, label: "45 min" },
  { value: 60, label: "60 min" },
  { value: 75, label: "1 hr 15 min" },
  { value: 90, label: "1 hr 30 min" },
  { value: 105, label: "1 hr 45 min" },
  { value: 120, label: "2 hrs" },
];

export const now = DateTime.now();

/**
 * calculates the time from now until some data's startDateTime
 * Used to show how long until an appointment
 * @param ad{start time}
 * @return {string|*}
 */

export const calculateTime = (ad) => {
  const start = DateTime.fromISO(ad);
  const diff = start.diff(now);
  const msMin = 60 * 1000; // milliseconds in Minute
  const msHour = msMin * 60; // milliseconds in Hour
  const msDay = msHour * 24; // milliseconds in day
  const seconds = Math.round(diff / 1000);
  const minutes = Math.round(diff / msMin);
  const hours = Math.round(diff / msHour);
  const daysLeft = Math.round(diff / msDay);

  if (diff < 0) {
    return "This appointment has passed";
  }
  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? "" : "s"} left`;
  }
  if (diff < msHour) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} left`;
  }
  if (diff < msDay) {
    return `${hours} hour${hours === 1 ? "" : "s"} left`;
  }
  if (!diff) {
    return null;
  }
  return `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;
};

/**
 * calculates age of pet based on current year
 * @param pa{pet age}
 * @return {number|*}
 */
export const calculateAge = (pa) => {
  const birthdate = DateTime.fromISO(pa);
  const age = now.year - birthdate.year;
  const m = now.month - birthdate.month;
  if (m < 0 || (m === 0 && now < birthdate)) {
    return age - 1;
  }
  return age;
};

/**
 * formats time string to AM or PM
 * @param date{date}
 * @return {string|*}
 */

export const formatTimeAmPm = (date) => {
  return date.toLocaleString("en-US", {
    hour: "numeric",
    minute: "numeric",
    hour12: true,
  });
};

/**
 * formats ISO time to AM or PM
 * @param dateString{string}
 * @return {string|*}
 */

export const formatTimeAmPmFromISO = (date) => {
  if (!date) return null;
  return DateTime.fromISO(date).toLocaleString(DateTime.TIME_SIMPLE)
};

/**
 * formats date to be MM/DD/YYYY
 * @param date{date}
 * @return {string|*}
 */

export const formatCalDate = (date) => {
  return DateTime.fromISO(date).toLocaleString();
};

/**
 * Used to format the incoming date string to a js date object to be displayed by the datepicker
 * @param dateString{string} format: 'YYYY/MM/DD'
 * @return {null|Date}
 */

export const formatStringToDate = (dateString) => {
  if (!dateString) return null;
  return DateTime.fromISO(dateString).toJSDate();
};

export const formatStringToShortDate = (dateString) => {
  if (!dateString) return null;
  return DateTime.fromISO(dateString).toJSDate();
};

/**
 * Parses a js date object to a date string that can be used for a 'date' object in rails.
 * Date objects in rails do not accept timestamps
 * @param v{Date}
 * @return {string|*}
 */
export const parseDateToString = (v) => {
  if (!v) return v;
  return DateTime.fromJSDate(v).toISODate();
};

/**
 * Parses a js date object to a date string with time that can be used for a 'date' object in rails.
 * Date objects in rails do not accept timestamps
 * @param v{Date}
 * @return {string|*}
 */
export const parseDateToStringWithTime = (v) => {
  if (!v) return v;
  return DateTime.fromJSDate(v).toISO();
};

export const parseDateToShortDateString = (v) => {
  if (!v) return v;
  return DateTime.fromJSDate(v).toLocaleString(DateTime.DATE_SHORT);
};
/**
 * formats date to milliseconds if date is not null
 * @param v{Date}
 * @return {Date}
 */

const AMOUNT_OF_MILLISECONDS_IN_SECONDS = 1000;

export const formatDateBackToMsecs = (v) =>
  !isNull(v) ? v * AMOUNT_OF_MILLISECONDS_IN_SECONDS : v;

/**
 * parses date to seconds
 * @param v{Date}
 * @return {Date}
 */

export const parseDateToSecs = (v) => {
  if (isNull(v)) return v;
  const timeInMs = v.getTime();
  const timeInSec = Math.floor(timeInMs / AMOUNT_OF_MILLISECONDS_IN_SECONDS);
  return timeInSec;
};

/**
 * parses date to milliseconds
 * @param v{Date}
 * @return {Date}
 */

export const parseDateToMsecs = (v) => {
  if (isNull(v)) return v;
  const timeInMs = v.getTime();
  return timeInMs;
};

/**
 * parses a JS date input into conventional Month, Day Year string
 * @param v{Date}
 * @return {Date}
 */

export const parseDateToMonth = (v) => {
  if (!v) return v;
  const month = months[v.getMonth()];
  const day = weekdays[v.getDay()];
  const date = v.getDate();
  let suffix = "";

  const dateSuffix = (n) => {
    switch (n % 10) {
      case 1:
        suffix = "st";
        break;
      case 2:
        suffix = "nd";
        break;
      case 3:
        suffix = "rd";
        break;
      default:
        suffix = "th";
    }
    if (n === 11 || n === 13) {
      suffix = "th";
    }
    return suffix;
  };

  const year = v.getFullYear();
  return `${day}, ${month} ${date}${dateSuffix(date)}, ${year}`;
};

/**
 * RETURNS DATE STRING IN ~~~~ WED, SEPT 31 ~~~~ FORMAT
 * @param date{Date}
 * @return {Date}
 */

export const formatDateToAbrvCal = (date) => {
  const d = DateTime.fromISO(date);
  return `${d.weekdayShort}, ${d.monthShort} ${d.day}`;
};

export const calculateAppointmentTime = (date, ts) => {
  const apptDay = DateTime.fromMillis(parseInt(date, 10));
  const startTime = DateTime.fromISO(ts.attributes.startDatetime);
  const endTime = DateTime.fromISO(ts.attributes.endDatetime);
  const { day, month, year } = apptDay;
  const start = startTime.set({ year, month, day });
  const end = endTime.set({ year, month, day });
  return {
    start,
    end,
  };
};

export const lastDayOfYear = `12/31/${new Date().getFullYear()}`;

export const generateTimeOptions = () => {
  const hours = [];

  const beginDay = DateTime.local().startOf("day");

  for (let i = 0; i < 96; i += 1) {
    const nextTime = beginDay.plus({
      hours: i / 4,
    });
    const isoTime = nextTime.toISO();
    const readableTime = nextTime.toLocaleString(DateTime.TIME_SIMPLE);
    hours.push({
      value: isoTime,
      label: readableTime,
    });
  }
  const endDay = DateTime.local()
    .endOf("day")
    .set({ second: 0, millisecond: 0 });
  hours.push({
    value: endDay.toISO(),
    label: endDay.toLocaleString(DateTime.TIME_SIMPLE),
  });
  return hours;
};

export const formatTime = (v) => {
  if (!v) return v;
  return DateTime.fromISO(v);
};

export const parseTime = (v) => {
  if (!v) return v;
  return v;
};
